/**
 * Component Loader Utilities
 * Handles dynamic importing of page components with Vite glob
 */

import { lazy } from 'react';

// Vite glob import - pre-load all page components
const pageModules = import.meta.glob('../../../pages/admin/**/page.jsx');

// One lazy component per path, for the lifetime of the app. The route tree is
// regenerated on EVERY features change (useAdminRoutes → generateAdminRoutes),
// and a fresh lazy() per regeneration makes the currently displayed page
// re-suspend: React resolves the new lazy before it can match it to the mounted
// fiber, so the route's Suspense boundary hides the page (fallback flash) and
// its class components get componentWillUnmount — which cancels any in-flight
// MUI transition (a mid-exit Backdrop then stays stuck at opacity 0, blocking
// the app). A stable component per path keeps element types identical across
// regenerations, so nothing re-suspends.
const lazyComponentCache = new Map();

/**
 * Dynamically import page component with fallback
 * Uses Vite's glob import for better tree-shaking
 * @param {string} path - Component path relative to pages directory
 * @returns {React.LazyExoticComponent} Lazy-loaded component (cached per path)
 */
export const importPageComponent = (path) => {
  if (lazyComponentCache.has(path)) return lazyComponentCache.get(path);
  const component = createPageComponent(path);
  lazyComponentCache.set(path, component);
  return component;
};

const createPageComponent = (path) => {
  return lazy(async () => {
    try {
      // Construct the full path for glob import lookup
      const fullPath = `../../../pages/${path}/page.jsx`;
      
      // Check if the module exists in our glob import
      if (pageModules[fullPath]) {
        const module = await pageModules[fullPath]();
        return module;
      }
      
      console.warn(`Module not found at ${fullPath}, using fallback`);
      // Return fallback component
      const fallbackModule = await import('../../../pages/admin/FeatureNotFound.jsx');
      return fallbackModule;
    } catch (error) {
      console.error(`Failed to load component at ${path}/page.jsx:`, error);
      
      // Return fallback component
      const fallbackModule = await import('../../../pages/admin/FeatureNotFound.jsx');
      return fallbackModule;
    }
  });
};

/**
 * Check if a page component exists at the given path
 * @param {string} componentPath - Component path to check
 * @returns {boolean} True if component exists
 */
export const hasPageComponent = (componentPath) => {
  const checkPath = `../../../pages/${componentPath}/page.jsx`;
  return !!pageModules[checkPath];
};

export { pageModules };
